/**
 * ProviderDocumentsPage — list documents for the provider's operations.
 * Backend already scopes results. PaymentAgents can verify/reject; ticket
 * providers can view + upload (verification is admin-only for tickets here).
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtime } from '@/contexts/RealtimeContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FileText, RefreshCw, Search } from 'lucide-react';
import { listDocuments } from '@/services/workflowApi';
import DocumentList from '@/components/workflow/DocumentList';

const ProviderDocumentsPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const { subscribe } = useRealtime();
  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listDocuments(getAuthHeaders, {});
      setDocs(r.documents || []);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const offs = [
      subscribe('document.uploaded', load),
      subscribe('document.verified', load),
    ];
    return () => offs.forEach((f) => f && f());
  }, [subscribe, load]);

  const filt = docs.filter((d) => !search || [d.filename, d.document_type, d.operation_id].some((v) => String(v || '').toLowerCase().includes(search.toLowerCase())));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Documentos
          </h1>
          <p className="text-xs text-slate-500">{docs.length} documentos en tus operaciones</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar" className="pl-7 h-8 w-56 text-xs" data-testid="provider-docs-search" />
          </div>
          <Button size="sm" variant="outline" onClick={load} data-testid="provider-docs-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" />Refrescar</Button>
        </div>
      </div>

      <Card className="p-4">
        {loading ? <p className="text-xs text-slate-400">Cargando…</p> :
          <DocumentList
            documents={filt}
            getAuthHeaders={getAuthHeaders}
            onChange={load}
            canVerify={user?.role === 'payment_agent'}
            canDelete={false}
          />
        }
      </Card>
    </div>
  );
};

export default ProviderDocumentsPage;
